import { observable } from 'mobx';

type KeyFields = { [key: string]: string | number | null };

export type BrandKey = string;

type CombinedKeyMapArgs<Key extends KeyFields, Value> = {
  data?: Array<[key: BrandKey, Value]> | Map<BrandKey, Value>;
  keyBuilder: Array<keyof Key> | ((keys: Key) => BrandKey);
};

export class CombinedKeyMap<Key extends KeyFields, Value = unknown> {
  static createKeyBuilder<Key extends KeyFields>(fields: Array<keyof Key>) {
    return (keys: Key) => {
      const values: Array<Key[keyof Key]> = [];
      fields.forEach((field) => {
        values.push(keys[field]);
      });

      return values.join('|') as BrandKey;
    };
  }

  #storage = observable.map(new Map<BrandKey, Value>());
  #keyBuilder: (keys: Key) => BrandKey;

  constructor(args: CombinedKeyMapArgs<Key, Value>) {
    const { data, keyBuilder } = args;

    this.#keyBuilder = Array.isArray(keyBuilder)
      ? CombinedKeyMap.createKeyBuilder(keyBuilder)
      : keyBuilder;

    if (data) {
      const content = data instanceof Map ? data : new Map(data);

      this.#storage = observable.map(content);
    }
  }

  getRecord(keys: Key) {
    const key = this.#keyBuilder(keys);

    return this.#storage.get(key);
  }

  setRecord(keys: Key, value: Value) {
    const key = this.#keyBuilder(keys);

    this.#storage.set(key, value);
  }

  get size() {
    return this.#storage.size;
  }

  clear() {
    this.#storage.clear();
  }

  createKey(keys: Key) {
    return this.#keyBuilder(keys);
  }

  values() {
    return this.#storage.values();
  }

  print() {
    console.log(this.#storage);
  }
}

/* Example:

```typescript
type KeyFields = {
  trainTypeId: number;
  tariffId: number;
}

const createKey = CombinedKeyMap.createKeyBuilder<KeyFields>(['trainTypeId', 'tariffId']);
const rawData = [
  {trainTypeId: 1, tariffId: 1, price: 100},
  {trainTypeId: 1, tariffId: 2, price: 150},
  {trainTypeId: 2, tariffId: 1, price: 200},
  {trainTypeId: 2, tariffId: 2, price: 250},
];

const combinedKeyMapContent = rawData.map((record) => {
  const key = createKey(record);
  return [key, record];
});

const combinedKeyMap = new CombinedKeyMap({
  data: combinedKeyMapContent,
  keyBuilder: createKey,
});

const record = combinedKeyMap.getRecord({trainTypeId: 1, tariffId: 1});

```
*/
